import { useCurrentUser } from '@bonusly/who-am-i';

import { useBizyUnreadNotifications } from '../../../queries/bizy-notifications';
import { useFetchNotifications } from '../../../queries/notifications';

/**
 * Returns whether the current user has an unseen Bizy notification.
 *
 * Drives the waving state + dot on the navbar Ask Bizy button. Backed by a
 * real React Query against `/api/v2/bizy/notifications/unread` that
 * refetches on window focus so a notification delivered while the user is
 * on the page surfaces without a hard reload. The indicator goes away when
 * `useBizyMarkNotificationsSeenMutation` invalidates the query.
 */
export function useHasBizyNotification(): boolean {
	const { data } = useBizyUnreadNotifications();
	return data?.hasUnread ?? false;
}

/**
 * Returns the count of unread proactive Bizy notifications. Drives the
 * numeric badge on the navbar Ask Bizy button (matches the announcement
 * counter pattern). Always returns 0 when Bizy is disabled or the query
 * hasn't resolved yet — never undefined.
 */
export function useBizyUnreadCount(): number {
	const { data } = useBizyUnreadNotifications();
	return data?.unreadCount ?? 0;
}

export function useBizyNotificationAttention(): { shouldSurface: boolean; unreadCount: number } {
	const { data } = useBizyUnreadNotifications();
	const user = useCurrentUser();
	const { unreadReceivedRecognitionCount, isSuccess } = useFetchNotifications(user.locale);
	const hasUnreadReceivedRecognition = unreadReceivedRecognitionCount > 0;
	const shouldSurface = (data?.hasUnread ?? false) && isSuccess && !hasUnreadReceivedRecognition;
	return {
		shouldSurface,
		unreadCount: shouldSurface ? (data?.unreadCount ?? 0) : 0,
	};
}

/**
 * Returns the conversation id of the most-recent unread proactive notification,
 * if any. The chat panel uses this to jump straight to Bizy's opening message
 * when the panel is opened with an unread notification.
 *
 * Returns null when:
 *  - There are no unread notifications.
 *  - The unread notification has no linked ChatHistory (rare edge case;
 *    earlier proactive entries pre-`chat_history_id` may not have one).
 */
export function useUnreadBizyConversationId(): string | null {
	const { data } = useBizyUnreadNotifications();
	return data?.latestConversationId ?? null;
}
